import { useDeferredValue, useMemo, type ReactNode } from "react";
import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import { DatesProvider, useDates } from "dates/DatesContext";
import { useModuleEnabled } from "redux/selectors/modules";
import { ORG_SUMMARY_REPORT_BREADCRUMB_SPEC, REPORT_LINKS } from "utils/dashboardLinks";
import { useGetV4PricingPlanReportByDate } from "../api/pricing_plan/v4PricingPlanReportByDate";
import { useQuery } from "@tanstack/react-query";
import { PageContainer } from "components/core/basic/PageContainer";
import MetricsCard from "components/custom/merchandise/MetricsCard";
import { buildMetric } from "views/merchandise/metrics";
import { useAppSelector } from "redux/hooks";
import { COMPARE_METRIC_LOOKUP } from "views/merchandise/tables/metricsByEntity";
import { type PricingPlanQueryV4Row } from "../api/pricing_plan/v4PricingPlanReportByPlan";
import { css } from "../../../../styled-system/css";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { usePersistedTableState } from "components/table/usePersistedTableState";
import { selectionStateToSelectedIds, idsToVisibilityState } from "components/table/utils";
import { useGetV4PricingPlanReportTotals } from "../api/pricing_plan/v4PricingPlanReportTotals";
import PricingPlanReportTable from "modules/perf_reports/pricing_plan/PricingPlanReportTable";

import NoImage from "assets/images/utility/no_image.png";
import { getPlanType } from "modules/pricingv2/utils";
import { getURLPrefix } from "utils/getUrlPrefix";
import TrellisLink from "components/core/basic/TrellisLink";
import { Col, Row, Badge } from "react-bootstrap";
import { FaExclamationTriangle } from "react-icons/fa";
import { trellisPalette } from "components/custom/analytics/palettes";
import { IconButton } from "@material-ui/core";
import { MdEdit } from "react-icons/md";
import { useModal } from "@ebay/nice-modal-react";
import { QuickPlanConfigModal } from "modules/pricingv2/components/modals/QuickPlanConfig";

const columnHelper = createColumnBuilder<PricingPlanQueryV4Row>();

const URL_PREFIX = getURLPrefix();

interface DashboardStats {
  id: number;
  status_reason?: string | undefined;
  enroll_date: string | undefined;
  is_auto: boolean | undefined;
  plan_type: string | undefined;
  configured_slider_value: number | undefined;
  tags: string[] | undefined;
}

const PricingPlanCell = (props: { original: DashboardStats; value: ReactNode }) => {
  const hasPriceDebugModule = useModuleEnabled("PRICE_DEBUG");
  const params = {
    plan_type: props.original.plan_type || "",
    is_auto: props.original.is_auto || false,
    current_program: undefined,
  };

  const planStrategy = getPlanType(params);
  const image = `${URL_PREFIX}/api/productimage/?pricingplan=${props.original.id}`;

  return (
    <div style={{ display: "flex", minWidth: "380px", alignItems: "left" }}>
      <div
        style={{
          userSelect: "none",
          WebkitUserSelect: "none",
          flexShrink: 0,
          width: 50,
          height: 50,
          marginRight: "10px",
          background: `url(${image}) no-repeat center / contain, url("${NoImage}") no-repeat center / contain`,
        }}
        draggable="false"
      />
      <Row>
        <Col className="text-start" xs={12}>
          <TrellisLink
            to={`/user/_pricing/plan/${props.original.id}`}
            style={{
              textDecoration: "none",
              fontWeight: "500",
              fontSize: "1.5rem",
              cursor: "pointer",
            }}
          >
            {props.value}
          </TrellisLink>
        </Col>
        <Col className="text-start" xs={12}>
          {hasPriceDebugModule &&
            props.original.status_reason !== undefined &&
            props.original.status_reason === "Price Mismatch" && (
              <span className="pe-2" style={{ color: trellisPalette[17] }}>
                <FaExclamationTriangle />
              </span>
            )}

          {planStrategy !== "Dynamic Price" && (
            <Badge
              className={`badge bg-trellis-lightgrey text-end me-3`}
              style={{
                borderRadius: "5px",
              }}
            >
              <span>{planStrategy}</span>
            </Badge>
          )}
          {planStrategy === "Dynamic Price" && (
            <Badge
              className={`badge bg-trellis-purple text-end me-3`}
              style={{
                borderRadius: "5px",
              }}
            >
              {props.original.enroll_date &&
                (props.original.configured_slider_value ?? 0) >= 0 &&
                (props.original.configured_slider_value ?? 0) <= 0 && <span>Balanced</span>}
              {props.original.enroll_date && (props.original.configured_slider_value ?? 0) < 0 && <span>Volume</span>}
              {props.original.enroll_date && (props.original.configured_slider_value ?? 0) > 0 && <span>Margin</span>}
              {!props.original.enroll_date && <span>n/a</span>}
            </Badge>
          )}

          {props.original.tags?.map((tag, index) => (
            <Badge
              className="bg-trellis-mauve"
              style={{
                marginLeft: "1rem",
                cursor: "pointer",
              }}
              pill
              key={`${tag}-${index}`}
            >
              {tag}
            </Badge>
          ))}
        </Col>
      </Row>
    </div>
    // </TrellisLink>
  );
};

const PricingPlanReportBody = () => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const { start, end, group } = useDates();
  const quickConfigModal = useModal(QuickPlanConfigModal);

  const standardMetricIds = useMemo(() => {
    return [
      "sales",
      "units",
      "page_views",
      "target_units",
      "target_sales",
      "inc_contribution",
      "inc_cashflow",
      "inc_units",
    ];
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: () => <span className={css({ marginRight: "auto" })}>Pricing Plan</span>,
        enableHiding: false,
        minSize: 100,
        size: 500,
        cell: (props) => {
          const original: DashboardStats = {
            id: props.row.original.pricing_plan_id,
            status_reason: props.row.original.status_reason ?? undefined,
            enroll_date: props.row.original.enroll_date ?? undefined,
            is_auto: props.row.original.is_auto ?? undefined,
            plan_type: props.row.original.plan_type ?? undefined,
            configured_slider_value: props.row.original.configured_slider_value ?? undefined,
            tags: props.row.original.tags ?? undefined,
          };

          return (
            <PricingPlanCell
              original={original}
              value={
                <span
                  className={css({
                    cursor: "pointer",
                    textWrap: "nowrap",
                    fontSize: "1.5rem",
                    fontWeight: "500",
                    overflow: "hidden",
                  })}
                >
                  {props.row.original.name}
                </span>
              }
            />
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: () => <span className={css({ marginRight: "auto" })}>Actions</span>,
        enableHiding: false,
        minSize: 100,
        size: 500,
        cell: (props) => {
          const original: DashboardStats = {
            id: props.row.original.pricing_plan_id,
            status_reason: props.row.original.status_reason ?? undefined,
            enroll_date: props.row.original.enroll_date ?? undefined,
            is_auto: props.row.original.is_auto ?? undefined,
            plan_type: props.row.original.plan_type ?? undefined,
            configured_slider_value: props.row.original.configured_slider_value ?? undefined,
            tags: props.row.original.tags ?? undefined,
          };

          return (
            <IconButton
              onClick={() => {
                void quickConfigModal.show({
                  planId: original.id,
                });
              }}
            >
              <MdEdit size={"1.5em"} color={trellisPalette[3]} />
            </IconButton>
          );
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "tags",
        header: "Tags",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "multi-select",
          },
        },
      }),
      columnHelper.accessor("category_id", {
        id: "category_id",
        header: "Category",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      // ...actionColumns,
      ...standardMetricIds.map((metricId) => {
        return columnHelper.metric(metricId);
      }),
    ],
    [standardMetricIds, quickConfigModal],
  );

  // Below is to hide additional metrics by default without having to define each one
  const defaultSelectedColumns = [
    "sales",
    "units",
    "page_views",
    "target_units",
    "target_sales",
    "inc_contribution",
    "inc_cashflow",
    "inc_units",
    "actions",
  ];
  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id as string),
  );

  const initialStateConfig = {
    columnVisibility: {
      ...defaultHiddenCols,
    },
    columnOrder: defaultSelectedColumns,
    columnPinning: {
      left: ["name"],
    },
    sorting: [{ id: "sales", desc: true }],
  };

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "pricingPlanreport",
    initialState: initialStateConfig,
  });

  const deferredSearch = useDeferredValue(state.globalFilter);
  const selectedIds = selectionStateToSelectedIds(state.rowSelection);
  const selectedIdsFilter =
    selectedIds.length ? [{ id: "pricing_plan_id", value: { op: "anyof", value: selectedIds } }] : [];

  // Fetch pricingPlan report data grouped by date to populate chart
  const byDateOptionsGetter = useGetV4PricingPlanReportByDate();
  const pricingPlanByDateReportQuery = useQuery(
    byDateOptionsGetter({
      filters: [...state.columnFilters, ...selectedIdsFilter],
      search: deferredSearch,
    }),
  );

  const totalOptionsGetter = useGetV4PricingPlanReportTotals();
  const pricingPlanTotalsReportQuery = useQuery(
    totalOptionsGetter({
      filters: [...state.columnFilters, ...selectedIdsFilter],
      search: deferredSearch,
    }),
  );

  const metrics = useMemo(() => {
    const data = {
      total: pricingPlanTotalsReportQuery.data?.data,
    };
    return standardMetricIds.map((metricId) =>
      buildMetric(marketPlace, data, metricId, COMPARE_METRIC_LOOKUP[metricId]),
    );
  }, [pricingPlanTotalsReportQuery.data?.data, standardMetricIds, marketPlace]);

  return (
    <PageContainer withBreadcrumb>
      <MetricsCard
        id={"pricingPlanReport"}
        title={"Performance Overview"}
        data={{ date: pricingPlanByDateReportQuery.data?.data ?? [], total: pricingPlanByDateReportQuery.data?.data }}
        loading={
          pricingPlanByDateReportQuery.isFetching &&
          (!pricingPlanByDateReportQuery.data || pricingPlanTotalsReportQuery.isPlaceholderData)
        }
        showLoadingSpinner
        showSkeletonLoadingEffect={
          pricingPlanByDateReportQuery.isFetching &&
          (!pricingPlanByDateReportQuery.data || pricingPlanTotalsReportQuery.isPlaceholderData)
        }
        metrics={metrics}
        showMetricsSelector
        initialSelectedMetric={["sales", "units", "page_views"]}
        group={group}
        showAnnotations={false}
        byline={
          <>
            {start.format("MMMM Do YYYY")}
            {" - "}
            {end.format("MMMM Do YYYY")}
          </>
        }
        context={"pricing_plan"}
        extendMetrics={false}
      >
        <PricingPlanReportTable state={state} initialState={initialState} setters={setters} columns={columns} />
      </MetricsCard>
    </PageContainer>
  );
};

export default function PricingPlanReport() {
  const hasOrgSummaryreportModule = useModuleEnabled("ORG_SUMMARY_REPORT");
  const breadCrumbItems = [
    {
      name: "Pricing Plan Report",
      href: "/user/report/v2/pricing_plan",
      dropdownLinkOptions: REPORT_LINKS,
      searchable: false,
      dropdownSubtitle: "Reports",
    },
  ];

  if (
    hasOrgSummaryreportModule &&
    !breadCrumbItems[0]?.dropdownLinkOptions.find((o) => o.name === ORG_SUMMARY_REPORT_BREADCRUMB_SPEC.name)
  ) {
    breadCrumbItems[0]?.dropdownLinkOptions.unshift(ORG_SUMMARY_REPORT_BREADCRUMB_SPEC);
  }
  return (
    <DatesProvider>
      <BreadcrumbDates items={breadCrumbItems} />
      <PricingPlanReportBody />
    </DatesProvider>
  );
}
